import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import BackBtn from "../../widgets/BackBtn";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import { toast } from "react-toastify";
import FullImageModal from "../../widgets/FullImageModal";
import useGetEventSettings from "../../hooks/useGetEventSettings";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse } from "../../utils/util";

// Import the new hook
import { useImageUpload } from "../../context/ImageUploadContext";

const IMAGE_SETTINGS_CONFIG = [
  {
    id: "socialWallBanner",
    title: "Social Wall Banner",
    description: "Displayed on the main Social Wall.",
    aspectRatio: 800 / 450,
  },
  {
    id: "loginBanner",
    title: "Login Banner",
    description: "Displayed on the login page.",
    aspectRatio: 1080 / 1080,
  },
  {
    id: "eventLogo",
    title: "Event Logo",
    description: "Displayed in the App Bar of the dashboard.",
    aspectRatio: 500 / 167,
  },
  {
    id: "studentBadgeBg",
    title: "Student Badge Background",
    description: "Used to preview the student badge.",
    aspectRatio: 1243 / 1757,
  },
  {
    id: "defProfileBanner",
    title: "Default Profile Banner",
    description: "Default banner for Visitor/Exhibitor profile details.",
    aspectRatio: 640 / 214,
  },
  {
    id: "eventBanner",
    title: "Event Banner",
    description: "Used in Forgot Password, Reset Password, etc.",
    aspectRatio: 780 / 237,
  },
];

const EventSettings = () => {
  const { data, isLoading, refetch, isError, error } = useGetEventSettings();
  const [showImage, setShowImage] = useState(false);
  const [imageToShow, setImageToShow] = useState("");

  // Access the global cropper
  const { openCropper } = useImageUpload();

  const [config, setConfig] = useState({
    socialWallBanner: "",
    loginBanner: "",
    eventLogo: "",
    studentBadgeBg: "",
    defProfileBanner: "",
    eventBanner: "",
  });

  useEffect(() => {
    if (data) {
      setConfig({
        socialWallBanner: data.socialWallBanner || "",
        loginBanner: data.loginBanner || "",
        eventLogo: data.eventLogo || "",
        defProfileBanner: data.defProfileBanner || "",
        eventBanner: data.eventBanner || "",
        studentBadgeBg: data.studentBadgeBg || "",
      });
    }
  }, [data]);

  const handleUploadSuccess = (response) => {
    toast.success(response.message);
    refetch();
  };

  const handleShowImage = (imgUrl) => {
    if (imgUrl) {
      setImageToShow(imgUrl);
      setShowImage(true);
    }
  };

  const handleOpenCropper = (type, ratio) => {
    // Just call the context function and pass the specific onSuccess handler
    openCropper({
      type: type,
      aspectRatio: ratio,
      onSuccess: handleUploadSuccess,
      onError: (err) => console.error("Upload error:", err),
    });
  };

  if (isLoading) return <MuPb />;
  if (isError) return <Alert severity="error">{getErrorResponse(error)}</Alert>;

  return (
    <Container maxWidth="md">
      <FullImageModal
        imgUrl={imageToShow}
        open={showImage}
        handleClose={() => setShowImage(false)}
      />

      <Box mb={2}>
        <BackBtn />
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Image Assets
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={4}>
          Manage branding and banner images for{" "}
          <strong>{data?.eventName}</strong>.
        </Typography>

        <Stack spacing={3}>
          {IMAGE_SETTINGS_CONFIG.map((setting) => {
            const currentImgUrl = config[setting.id];

            return (
              <Card
                key={setting.id}
                variant="outlined"
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Box
                  onClick={() => handleShowImage(currentImgUrl)}
                  sx={{
                    width: 120,
                    height: 80,
                    flexShrink: 0,
                    bgcolor: "grey.100",
                    border: "1px dashed",
                    borderColor: "grey.400",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: currentImgUrl ? "pointer" : "default",
                    overflow: "hidden",
                    "&:hover": { opacity: currentImgUrl ? 0.8 : 1 },
                  }}
                >
                  {currentImgUrl ? (
                    <img
                      src={currentImgUrl}
                      alt={setting.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <InsertPhotoOutlinedIcon color="disabled" />
                  )}
                </Box>

                <Box
                  sx={{ flexGrow: 1, textAlign: { xs: "center", sm: "left" } }}
                >
                  <Typography variant="subtitle1" fontWeight="600">
                    {setting.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {setting.description}
                  </Typography>
                </Box>

                <Button
                  onClick={() =>
                    handleOpenCropper(setting.id, setting.aspectRatio)
                  }
                  variant="outlined"
                  startIcon={<EditOutlinedIcon />}
                  size="small"
                  sx={{ flexShrink: 0 }}
                >
                  {currentImgUrl ? "Change" : "Upload"}
                </Button>
              </Card>
            );
          })}
        </Stack>
      </Paper>
    </Container>
  );
};

export default EventSettings;
